import { TFunction } from 'i18next';
import { Country } from 'store/common/models';
import { replaceEnglishVatTerm, toArabicIndicDigits } from './arabicDigits';
import {
  getCountryLabelFromCountries,
  isEuropeanUnionCountryIsoOrName,
  localizeCountryInCompositeLabel,
} from './countryLabel';
import { resolveLocale } from './locales';

/** Slug used for `returns.type-names.{slug}` i18n keys. */
export function toReturnTypeSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\(([^)]+)\)/g, (_, inner: string) => ` ${inner.trim()} `)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Exact English API names → i18n key (also handles known aliases). */
const RETURN_TYPE_NAME_KEYS: Record<string, string> = {
  'UK VAT Return': 'vat-forms.uk-vat-return',
  'HMRC VAT Return': 'returns.type-names.hmrc-vat-return',
  'Article 10 VAT Return': 'returns.type-names.article-10-vat-return',
  'Article 11 VAT Return': 'returns.type-names.article-11-vat-return',
  'Return of Trading Details (RTD)':
    'returns.type-names.return-of-trading-details-rtd',
  'Return of Trading Details':
    'returns.type-names.return-of-trading-details-rtd',
  'Annual Return of Trading Details (RTD)':
    'returns.type-names.annual-return-of-trading-details',
  'Annual Return of Trading Details':
    'returns.type-names.annual-return-of-trading-details',
  'VAT3 Return': 'returns.type-names.vat3-return',
  'Form VAT3': 'returns.type-names.vat3-return',
  VAT3: 'returns.type-names.vat3-return',
  'Detailed VAT Return': 'returns.type-names.detailed-vat-return',
  'Recapitulative Statement': 'returns.type-names.recapitulative-statement',
  'Recapitulative Statement (EC Sales Listing)':
    'returns.type-names.recapitulative-statement-ec-sales',
  'VAT Recapitulative Statement':
    'returns.type-names.recapitulative-statement',
  'Recapitulative Return': 'returns.type-names.recapitulative-return',
  'EC Sales List': 'returns.type-names.ec-sales-list',
  'EC Sales Listing': 'returns.type-names.ec-sales-list',
  'VIES Return': 'returns.type-names.vies-return',
  'VAT Return': 'returns.type-names.vat-return',
  'Vat Return': 'returns.type-names.vat-return',
  'Return (Form VAT201)': 'returns.type-names.vat-return-form-vat201',
  'VAT Return (Form VAT201)': 'returns.type-names.vat-return-form-vat201',
  'VAT Return (Form VAT 201)': 'returns.type-names.vat-return-form-vat201',
  'Return (Form VAT 201)': 'returns.type-names.vat-return-form-vat201',
  'Standard VAT Return': 'returns.type-names.standard-vat-return',
  'Malta VAT Return': 'returns.type-names.malta-vat-return',
  'Cyprus VAT Return': 'returns.type-names.cyprus-vat-return',
  'UAE VAT Return': 'returns.type-names.uae-vat-return',
  'Netherlands VAT Return': 'returns.type-names.netherlands-vat-return',
  'Isle of Man VAT Return': 'returns.type-names.isle-of-man-vat-return',
  'Jersey GST Return': 'returns.type-names.jersey-gst-return',
  'GST Return': 'returns.type-names.gst-return',
  'VAT 201': 'returns.type-names.vat-201',
  'Form VAT201': 'returns.type-names.vat-201',
  'Form VAT 201': 'returns.type-names.vat-201',
  VAT201: 'returns.type-names.vat-201',
  'South Africa VAT 201': 'returns.type-names.vat-201',
  'South Africa VAT Return': 'returns.type-names.south-africa-vat-return',
  'One Stop Shop Return': 'returns.type-names.oss-return',
  'OSS Return': 'returns.type-names.oss-return',
  'EU OSS Return': 'returns.type-names.oss-return',
  'EU OSS': 'returns.type-names.oss-return',
  'EU OSS (One-Stop Shop)': 'returns.type-names.oss-return',
  'EU OSS (One Stop Shop)': 'returns.type-names.oss-return',
  'Intrastat Return': 'returns.type-names.intrastat-return',
};

const RETURN_TYPE_NAME_KEYS_LOWER = Object.fromEntries(
  Object.entries(RETURN_TYPE_NAME_KEYS).map(([name, key]) => [
    name.toLowerCase(),
    key,
  ])
);

const RETURN_TYPE_ID_KEYS: Record<string, string> = {};
if (typeof process !== 'undefined' && process.env.OSS_ID) {
  RETURN_TYPE_ID_KEYS[process.env.OSS_ID] = 'returns.type-names.oss-return';
}

/** Sentinel so missing keys aren't mistaken for translations after digit post-processing. */
const MISSING_KEY_SENTINEL = '__return_type_missing__';

const OSS_RETURN_KEYS = new Set([
  'returns.type-names.oss-return',
  'returns.type-names.oss-return-short',
]);

function applyArabicReturnNameFallback(name: string, language?: string): string {
  if (resolveLocale(language) !== 'ar') return name;
  return toArabicIndicDigits(replaceEnglishVatTerm(name));
}

function translateReturnTypeKey(
  i18nKey: string,
  fallbackName: string,
  t: TFunction,
  language?: string
): string {
  const translated = t(i18nKey, { defaultValue: MISSING_KEY_SENTINEL });
  if (
    !translated ||
    translated === MISSING_KEY_SENTINEL ||
    translated === i18nKey ||
    translated.startsWith('returns.type-names.')
  ) {
    return applyArabicReturnNameFallback(fallbackName, language);
  }
  return translated;
}

/** Translate a single return-type name from the API. */
export function getReturnTypeNameLabel(
  name: string | undefined,
  t: TFunction,
  language?: string,
  returnTypeId?: string
): string {
  if (!name?.trim()) return '';

  const trimmed = name.trim();

  if (returnTypeId && RETURN_TYPE_ID_KEYS[returnTypeId]) {
    return translateReturnTypeKey(
      RETURN_TYPE_ID_KEYS[returnTypeId],
      trimmed,
      t,
      language
    );
  }

  const exactKey =
    RETURN_TYPE_NAME_KEYS[trimmed] ??
    RETURN_TYPE_NAME_KEYS_LOWER[trimmed.toLowerCase()];
  if (exactKey) {
    return translateReturnTypeKey(exactKey, trimmed, t, language);
  }

  const slugKey = `returns.type-names.${toReturnTypeSlug(trimmed)}`;
  return translateReturnTypeKey(slugKey, trimmed, t, language);
}

/** Translate `{country} - {return name}` labels shown in tables and headers. */
export function localizeReturnMappingLabel(
  label: string | undefined,
  countries: Country[] | undefined,
  language: string,
  t: TFunction
): string {
  if (!label) return '';

  const separatorIndex = label.indexOf(' - ');
  if (separatorIndex === -1) {
    return getReturnTypeNameLabel(label, t, language);
  }

  const countryPart = label.slice(0, separatorIndex);
  const returnPart = label.slice(separatorIndex + 3);
  const localizedCountry = getCountryLabelFromCountries(
    countryPart,
    countries,
    language,
    countryPart
  );

  let returnKey: string | undefined;
  const exactKey =
    RETURN_TYPE_NAME_KEYS[returnPart.trim()] ??
    RETURN_TYPE_NAME_KEYS_LOWER[returnPart.trim().toLowerCase()];
  if (exactKey) {
    returnKey =
      isEuropeanUnionCountryIsoOrName(countryPart) &&
      OSS_RETURN_KEYS.has(exactKey)
        ? 'returns.type-names.oss-return-short'
        : exactKey;
  } else if (
    isEuropeanUnionCountryIsoOrName(countryPart) &&
    toReturnTypeSlug(returnPart).includes('oss')
  ) {
    returnKey = 'returns.type-names.oss-return-short';
  }

  const localizedReturn = returnKey
    ? translateReturnTypeKey(returnKey, returnPart, t, language)
    : getReturnTypeNameLabel(returnPart, t, language);

  return `${localizedCountry} - ${localizedReturn}`;
}

/** Backwards-compatible helper when only country localization is needed. */
export function localizeReturnMappingLabelOrCountry(
  label: string | undefined,
  countries: Country[] | undefined,
  language: string,
  t?: TFunction
): string {
  if (t) {
    return localizeReturnMappingLabel(label, countries, language, t);
  }
  return localizeCountryInCompositeLabel(label, countries, language);
}
